import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Image, SimpleGrid, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {MenuCard} from './menu-card'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Link as RouteLink} from 'react-router-dom'
import {getContentstackLink, getGlobalLink} from '../../utils/contentstack-utils'
import {remCalc} from '../../theme/_utils'

function mapTypeToVariant(type) {
    if (type === 'firstLevelPromo') return 'promo'
    if (type === 'subCategory') return 'default'
    return type
}

const MenuCardGroup = ({group, categoriesData, type, compact = false}) => {
    const styles = useMultiStyleConfig('ListMenu')

    const isPromo = type === 'promo' || type === 'firstLevelPromo'
    const isSubCategory = type === 'subCategory'
    const isFilter = type === 'filter'

    let numColumns = 3
    if (isPromo) numColumns = 4
    if (isSubCategory) numColumns = 2
    if (compact) numColumns = 2

    let header = null

    if (isFilter) {
        header = (
            <Box {...styles.filterHeaderContainer} sx={styles.filterHeaderContainerBreakpoint}>
                <Text {...styles.filterHeaderText}>{group?.filter_by_label}</Text>
                {group?.show_tag && (
                    <Flex
                        {...styles.filterheaderTag}
                        sx={{
                            color: group.tag.tag_label_hex_color
                                ? group.tag.tag_label_hex_color
                                : 'button.text.primary',
                            backgroundColor: group.tag.tag_hex_background_color
                                ? group.tag.tag_hex_background_color
                                : 'background.light-blue',
                        }}
                    >
                        {group?.tag?.tag_icon?.url && (
                            <Image
                                src={group?.tag?.tag_icon?.url}
                                height={remCalc(18)}
                                width={remCalc(18)}
                            />
                        )}
                        {group?.tag?.tag_label}
                    </Flex>
                )}
            </Box>
        )
    } else if (isSubCategory || type === 'firstLevelPromo') {
        const {url, displayName} = getContentstackLink(group?.redirects, categoriesData)
        header = (
            <Box {...styles.subCategoryHeaderContainer}>
                <Text {...styles.subCategoryHeaderText}>{displayName}</Text>
                <Link as={RouteLink} to={url} {...styles.subCategoryHeaderLink}>
                    {group?.view_all_label}
                </Link>
            </Box>
        )
    }

    let cards = group?.cards
    if (isPromo) {
        if (compact && type === 'firstLevelPromo') {
            cards = cards?.filter((card) => card.showinmobilefirstlevel).slice(0, 4)
        } else {
            cards = cards?.slice(0, 8)
        }
    } else if (isSubCategory) {
        cards = cards?.slice(0, 4)
    } else {
        cards = cards?.slice(0, 6)
    }

    return (
        <Box pos="relative">
            {header}
            <SimpleGrid columns={numColumns} {...styles.cardGroupContainer}>
                {cards?.map((card) => {
                    let label = null
                    let url = null
                    let isAbsolute = false
                    // promo and filter use the global field for redirects
                    if (isPromo || isFilter) {
                        const globalLink = getGlobalLink(card?.redirects, categoriesData)
                        url = globalLink?.url
                        label = globalLink?.label
                        isAbsolute = globalLink?.isAbsolute
                    } else {
                        // the others use the old one still
                        const oldGlobalLink = getContentstackLink(card?.redirects, categoriesData)
                        url = oldGlobalLink?.url
                        label = oldGlobalLink?.displayName ?? card?.link?.title
                    }

                    let image = card?.icon ?? card?.image
                    // for filter we have to check the type of the image to use
                    if (isFilter) {
                        if (card?.type === 'Icon') {
                            image = card?.icon
                        } else if (card?.type === 'Image') {
                            image = card?.image
                        }
                    }
                    return (
                        <MenuCard
                            key={card.uid}
                            title={label}
                            url={url ?? card?.link?.href}
                            image={image}
                            isLink
                            isAbsolute={isAbsolute}
                            variant={
                                isFilter && card?.type === 'icon'
                                    ? 'filterIcon'
                                    : mapTypeToVariant(type)
                            }
                        />
                    )
                })}
            </SimpleGrid>
        </Box>
    )
}

MenuCardGroup.propTypes = {
    group: PropTypes.object,
    categoriesData: PropTypes.object,
    type: PropTypes.string,
    compact: PropTypes.bool,
}

export {MenuCardGroup}
